/**
 * Assignment collaboration (client).
 *
 * Two surfaces:
 *   - Chat: one thread with three visibilities — public (Student), internal (staff), private (My Notes).
 *   - Flags: staff workflow items with a status (open → addressed → resolved).
 */

export const COLLABORATION_COMMENT_TYPES = Object.freeze({
  FORMS_CHAT: 'forms_chat',
  FORMS_FLAG: 'forms_flag',
  WORK_SAMPLE_FLAG: 'work_sample_flag'
});

export const COLLABORATION_CHANNELS = Object.freeze({
  CHAT: 'chat',
  FLAGS: 'flags'
});

/** Chat message audience. Notes are simply private-visibility chat messages. */
export const COLLABORATION_VISIBILITY = Object.freeze({
  PUBLIC: 'public', // Public tab — assignee + staff
  INTERNAL: 'internal', // staff only
  PRIVATE: 'private' // author only ("My Notes")
});

export const COLLABORATION_FLAG_STATUS = Object.freeze({
  OPEN: 'open',
  ADDRESSED: 'addressed',
  RESOLVED: 'resolved'
});

export const CHANNEL_COMMENT_TYPES = Object.freeze({
  [COLLABORATION_CHANNELS.CHAT]: [COLLABORATION_COMMENT_TYPES.FORMS_CHAT],
  [COLLABORATION_CHANNELS.FLAGS]: [
    COLLABORATION_COMMENT_TYPES.FORMS_FLAG,
    COLLABORATION_COMMENT_TYPES.WORK_SAMPLE_FLAG
  ]
});

export const DEFAULT_VISIBILITY_BY_TYPE = Object.freeze({
  [COLLABORATION_COMMENT_TYPES.FORMS_CHAT]: COLLABORATION_VISIBILITY.PUBLIC,
  [COLLABORATION_COMMENT_TYPES.FORMS_FLAG]: COLLABORATION_VISIBILITY.INTERNAL,
  [COLLABORATION_COMMENT_TYPES.WORK_SAMPLE_FLAG]: COLLABORATION_VISIBILITY.INTERNAL
});

export const VISIBILITY_LABELS = Object.freeze({
  [COLLABORATION_VISIBILITY.PUBLIC]: 'Public',
  [COLLABORATION_VISIBILITY.INTERNAL]: 'Internal',
  [COLLABORATION_VISIBILITY.PRIVATE]: 'My Notes'
});

export const CHAT_TAB_LABELS = Object.freeze({
  [COLLABORATION_VISIBILITY.PUBLIC]: 'Public',
  [COLLABORATION_VISIBILITY.INTERNAL]: 'Internal',
  [COLLABORATION_VISIBILITY.PRIVATE]: 'My Notes'
});

/** Shown under chat tabs when more than one visibility tab is available. */
export const CHAT_TAB_BANNERS = Object.freeze({
  [COLLABORATION_VISIBILITY.PUBLIC]:
    'Public is usually for the assignee (often a student) and staff with the right permissions to view and communicate together.',
  [COLLABORATION_VISIBILITY.INTERNAL]:
    'Internal is usually for staff and admins to discuss this assignment without the assignee seeing the thread.',
  [COLLABORATION_VISIBILITY.PRIVATE]:
    'My Notes are usually for your personal notes on this assignment — only you can see what you write here.'
});

export const CHAT_MULTI_SUBMISSION_BANNER =
  'Each submission attempt has its own chat thread. Select a different attempt in the sidebar to view or post messages for that attempt.';

export const FLAG_STATUS_LABELS = Object.freeze({
  [COLLABORATION_FLAG_STATUS.OPEN]: 'Open',
  [COLLABORATION_FLAG_STATUS.ADDRESSED]: 'Addressed',
  [COLLABORATION_FLAG_STATUS.RESOLVED]: 'Resolved'
});

/** Flag panel tabs — General (assignment workflow) vs Work Sample (VDM compliance). */
export const FLAG_TABS = Object.freeze({
  GENERAL: 'general',
  WORK_SAMPLE: 'work_sample'
});

export const FLAG_TAB_LABELS = Object.freeze({
  [FLAG_TABS.GENERAL]: 'General',
  [FLAG_TABS.WORK_SAMPLE]: 'Work Sample'
});

export const FLAG_TAB_BANNERS = Object.freeze({
  [FLAG_TABS.GENERAL]:
    'General flags are for assignment workflow — reminders, grading notes, or items that do not belong on the Work Sample compliance tracker.',
  [FLAG_TABS.WORK_SAMPLE]:
    'Work Sample flags are for VDM compliance review. They appear in Work Samples and follow the correction SLA. Select every checklist item that applies.'
});

export const FLAG_EVIDENCE_REMOVED_BANNER =
  'This submission is no longer included in the portfolio evidence list. Existing Work Sample flags are kept for audit; add new compliance flags only after the submission is back in the portfolio.';

export function normalizeCollaborationVisibility(value) {
  const v = String(value || '')
    .trim()
    .toLowerCase();
  if (Object.values(COLLABORATION_VISIBILITY).includes(v)) return v;
  return null;
}

export function normalizeFlagStatus(value) {
  const v = String(value || '')
    .trim()
    .toLowerCase();
  if (v === 'waived') return COLLABORATION_FLAG_STATUS.RESOLVED;
  if (Object.values(COLLABORATION_FLAG_STATUS).includes(v)) return v;
  return COLLABORATION_FLAG_STATUS.OPEN;
}

export function allowedVisibilitiesForType(type) {
  switch (type) {
    case COLLABORATION_COMMENT_TYPES.FORMS_CHAT:
      return [
        COLLABORATION_VISIBILITY.PUBLIC,
        COLLABORATION_VISIBILITY.INTERNAL,
        COLLABORATION_VISIBILITY.PRIVATE
      ];
    case COLLABORATION_COMMENT_TYPES.FORMS_FLAG:
    case COLLABORATION_COMMENT_TYPES.WORK_SAMPLE_FLAG:
      return [COLLABORATION_VISIBILITY.INTERNAL];
    default:
      return [COLLABORATION_VISIBILITY.PUBLIC];
  }
}
